import { formatLabel } from '../utils/formatText';
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, ChevronRight, ZoomIn, Shield, Package, Phone, Share2, Check, Layers, Plus, Minus, ShoppingCart, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useQuoteCart } from '../context/QuoteCartContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WhatsAppIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" className={`${className} fill-current`}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export const Produto = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { settings } = useSiteSettings();
  const { addProduct, addVariation, isInCart, getCartItem, totalItems } = useQuoteCart();
  
  const [product, setProduct] = useState(null);
  const [brand, setBrand] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Estado para seleção de variações
  const [selectedVariations, setSelectedVariations] = useState({}); // { variationId: quantity }
  const [simpleQuantity, setSimpleQuantity] = useState(1);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${BACKEND_URL}/api/products/by-slug/${slug}`);
        setProduct(response.data);

        // Buscar marca
        const brandsRes = await axios.get(`${BACKEND_URL}/api/brands`);
        const foundBrand = brandsRes.data.find(b => b.name?.toLowerCase() === response.data.brand?.toLowerCase());
        setBrand(foundBrand);

        // Buscar produtos relacionados
        const productsRes = await axios.get(`${BACKEND_URL}/api/products`);
        const related = productsRes.data
          .filter(p => p.category === response.data.category && p.id !== response.data.id)
          .slice(0, 4);
        setRelatedProducts(related);

        // Track view
        await axios.post(`${BACKEND_URL}/api/track/product/${response.data.id}`).catch(() => {});
      } catch (error) {
        console.error('Erro ao carregar produto:', error);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchProduct();
      setSelectedVariations({});
      setSimpleQuantity(1);
    }
  }, [slug]);

  const handleShare = async () => {
    const productUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(productUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: "Link copiado!" });
    } catch (err) {
      toast({ title: "Erro ao copiar", variant: "destructive" });
    }
  };

  const handleWhatsApp = () => {
    let productText = product.name;
    const selectedIds = Object.keys(selectedVariations);
    if (selectedIds.length > 0) {
      const names = selectedIds.map(id => {
        const v = product.variations.find(v => v.id === id);
        return v ? `${v.name} (${selectedVariations[id]})` : '';
      }).filter(Boolean).join(', ');
      productText += ` - ${names}`;
    }
    const message = encodeURIComponent(`Olá! Tenho interesse no produto: ${productText}`);
    window.open(`https://wa.me/${settings.site_whatsapp}?text=${message}`, '_blank');
  };

  // Variação selecionada/quantidade
  const toggleVariation = (variationId) => {
    setSelectedVariations(prev => {
      if (prev[variationId]) {
        const { [variationId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [variationId]: 1 };
    });
  };

  const updateVariationQuantity = (variationId, delta) => {
    setSelectedVariations(prev => {
      const current = prev[variationId] || 0;
      const newQty = Math.max(1, current + delta);
      return { ...prev, [variationId]: newQty };
    });
  };

  // Adicionar ao orçamento
  const handleAddToQuote = () => {
    const hasVariations = product.variations && product.variations.length > 0;
    
    if (hasVariations) {
      const selectedIds = Object.keys(selectedVariations);
      if (selectedIds.length === 0) {
        toast({ title: "Selecione pelo menos uma opção", variant: "destructive" });
        return;
      }
      
      selectedIds.forEach(varId => {
        const variation = product.variations.find(v => v.id === varId);
        const qty = selectedVariations[varId];
        if (variation && qty > 0) {
          addVariation(product, variation, qty);
        }
      });
      
      toast({ 
        title: `${selectedIds.length} ${selectedIds.length === 1 ? 'item adicionado' : 'itens adicionados'} ao orçamento!`,
        description: "Vá para o orçamento para finalizar"
      });
    } else {
      addProduct(product, simpleQuantity);
      toast({ 
        title: `${product.name} adicionado ao orçamento!`,
        description: `Quantidade: ${simpleQuantity}`
      });
    }
  };

  // Ir para orçamento
  const goToQuote = () => {
    handleAddToQuote();
    navigate('/orcamento');
  };

  // Total selecionado
  const hasVariations = product?.variations && product.variations.length > 0;
  const totalSelectedQty = hasVariations 
    ? Object.values(selectedVariations).reduce((a, b) => a + b, 0)
    : simpleQuantity;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#EA1266] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <Package size={56} className="mx-auto text-gray-300 mb-4" />
          <h1 className="text-2xl font-medium text-[#0A0A0A] mb-2">Produto não encontrado</h1>
          <p className="text-[#4B5563] font-medium mb-6">O produto que você está procurando não existe ou foi removido.</p>
          <Link to="/catalogo">
            <Button className="bg-[#EA1266] hover:bg-[#d00f58] rounded-full">Ver Catálogo</Button>
          </Link>
        </div>
      </div>
    );
  }

  const images = product.images?.length > 0 ? product.images : ['https://placehold.co/800x800/F5F5F5/333?text=Produto'];
  const productUrl = window.location.href;
  const productImage = images[0];

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="produto-page">
      <Helmet>
        <title>{product.name} | Disneylândia Cosméticos - Canaã dos Carajás</title>
        <meta name="description" content={`${product.name} - ${product.description?.slice(0, 150)}. Compre na Disneylândia Cosméticos em Canaã dos Carajás.`} />
        <link rel="canonical" href={productUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.name} | Disneylândia Cosméticos`} />
        <meta property="og:description" content={product.description?.slice(0, 200)} />
        <meta property="og:image" content={productImage} />
        <meta property="og:url" content={productUrl} />
      </Helmet>

      <div className="container mx-auto px-4 md:px-12 py-8">
        {/* Navegação */}
        <div className="flex items-center justify-between mb-6">
          <nav className="flex items-center gap-2 text-sm text-[#4B5563] font-medium">
            <Link to="/" className="hover:text-[#EA1266] transition-colors">Início</Link>
            <span>/</span>
            <Link to="/catalogo" className="hover:text-[#EA1266] transition-colors">Catálogo</Link>
            <span>/</span>
            <span className="text-[#0A0A0A] font-medium truncate max-w-[200px]">{product.name}</span>
          </nav>
          
          {totalItems > 0 && (
            <Link to="/orcamento">
              <Button size="sm" className="bg-[#EA1266] hover:bg-[#d00f58] text-white rounded-full">
                <ShoppingCart size={16} className="mr-2" />
                Orçamento ({totalItems})
              </Button>
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Galeria */}
          <div className="space-y-4">
            <div className="relative aspect-square rounded-3xl overflow-hidden bg-white shadow-lg cursor-zoom-in" onClick={() => setIsZoomed(!isZoomed)}>
              <img
                src={images[currentImageIndex]}
                alt={product.name}
                className={`w-full h-full object-contain transition-transform duration-300 ${isZoomed ? 'scale-150' : ''}`}
              />
              {images.length > 1 && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); prevImage(); }} className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 shadow-lg flex items-center justify-center hover:bg-white">
                    <ChevronLeft size={24} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); nextImage(); }} className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 shadow-lg flex items-center justify-center hover:bg-white">
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
              <button className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white/90 shadow flex items-center justify-center">
                <ZoomIn size={18} />
              </button>
            </div>

            {images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto py-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImageIndex(i)}
                    className={`w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all ${i === currentImageIndex ? 'border-[#EA1266]' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Detalhes */}
          <div className="space-y-6">
            {/* Header */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                {brand?.logo && (
                  <img src={brand.logo} alt={brand.name} className="h-8 object-contain" />
                )}
                <Badge className="bg-[#0A0A0A] text-white rounded-full">{formatLabel(product.brand)}</Badge>
                {product.is_promo && <Badge className="bg-[#EA1266] text-white">Promoção</Badge>}
              </div>
              <h1 className="text-3xl lg:text-4xl font-medium text-[#0A0A0A]">{product.name}</h1>
              {product.size && (
                <p className="text-gray-500 mt-2">Tamanho: {product.size}</p>
              )}
            </div>

            {/* Descrição */}
            <div className="prose prose-sm max-w-none">
              <p className="text-[#4B5563] leading-relaxed">{product.description || 'Entre em contato para mais informações sobre este produto.'}</p>
            </div>

            {/* Variações - seleção com quantidade */}
            {hasVariations && (
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <Layers size={20} className="text-[#EA1266]" />
                  <h3 className="text-lg font-semibold text-[#0A0A0A]">Selecione as Opções</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">Escolha uma ou mais opções e defina a quantidade de cada:</p>
                
                <div className="space-y-3 max-h-[350px] overflow-y-auto">
                  {product.variations.map((variation) => {
                    const isSelected = selectedVariations[variation.id] !== undefined;
                    const qty = selectedVariations[variation.id] || 0;
                    const alreadyInCart = isInCart(product.id, variation.id);
                    const cartItem = getCartItem(product.id, variation.id);
                    
                    return (
                      <div 
                        key={variation.id}
                        className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                          isSelected ? 'border-[#EA1266] bg-pink-50' : 'border-gray-100 bg-gray-50 hover:border-gray-200'
                        }`}
                      >
                        {variation.image && (
                          <div className="w-16 h-16 rounded-xl overflow-hidden bg-white flex-shrink-0 border">
                            <img src={variation.image} alt={variation.name} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#0A0A0A]">{variation.name}</p>
                          {variation.size && <p className="text-sm text-gray-500">{variation.size}</p>}
                          {variation.description && <p className="text-xs text-gray-400 mt-1">{variation.description}</p>}
                          {alreadyInCart && (
                            <p className="text-xs text-[#64BC46] mt-1">✓ Já no orçamento ({cartItem?.quantity})</p>
                          )}
                        </div>
                        
                        {isSelected ? (
                          <div className="flex items-center gap-2">
                            <button 
                              onClick={() => updateVariationQuantity(variation.id, -1)}
                              className="w-8 h-8 rounded-full bg-white border flex items-center justify-center hover:bg-gray-50 transition-all touch-manipulation active:scale-95"
                              type="button"
                              aria-label="Diminuir quantidade"
                            >
                              <Minus size={16} />
                            </button>
                            <span className="w-10 text-center font-bold">{qty}</span>
                            <button 
                              onClick={() => updateVariationQuantity(variation.id, 1)}
                              className="w-8 h-8 rounded-full bg-white border flex items-center justify-center hover:bg-gray-50 transition-all touch-manipulation active:scale-95"
                              type="button"
                              aria-label="Aumentar quantidade"
                            >
                              <Plus size={16} />
                            </button>
                            <button 
                              onClick={() => toggleVariation(variation.id)}
                              className="w-8 h-8 rounded-full bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 ml-2 transition-all touch-manipulation active:scale-95"
                              type="button"
                              aria-label="Remover"
                            >
                              <span className="text-sm font-bold">×</span>
                            </button>
                          </div>
                        ) : (
                          <button 
                            onClick={() => toggleVariation(variation.id)}
                            className="px-4 py-2 rounded-full bg-[#EA1266] text-white text-sm font-medium hover:bg-[#d00f58] active:bg-[#b80d4a] transition-all touch-manipulation active:scale-95"
                            type="button"
                          >
                            Selecionar
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {Object.keys(selectedVariations).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                    <span className="font-medium text-gray-700">
                      {Object.keys(selectedVariations).length} {Object.keys(selectedVariations).length === 1 ? 'opção selecionada' : 'opções selecionadas'}
                    </span>
                    <span className="font-bold text-[#EA1266]">
                      {totalSelectedQty} {totalSelectedQty === 1 ? 'unidade' : 'unidades'}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Produto simples - quantidade */}
            {!hasVariations && (
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-[#0A0A0A]">Quantidade:</span>
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setSimpleQuantity(Math.max(1, simpleQuantity - 1))}
                      className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
                    >
                      <Minus size={18} />
                    </button>
                    <span className="w-12 text-center font-bold text-xl">{simpleQuantity}</span>
                    <button 
                      onClick={() => setSimpleQuantity(simpleQuantity + 1)}
                      className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                </div>
                {isInCart(product.id) && (
                  <p className="text-sm text-[#64BC46] mt-3">✓ Já está no orçamento ({getCartItem(product.id)?.quantity} unidades)</p>
                )}
              </div>
            )}

            {/* Garantias */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl">
                <Shield size={24} className="text-[#64BC46]" />
                <div>
                  <p className="font-semibold text-sm text-[#0A0A0A]">100% Original</p>
                  <p className="text-xs text-gray-500">Produto autêntico</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-pink-50 rounded-xl">
                <Package size={24} className="text-[#EA1266]" />
                <div>
                  <p className="font-semibold text-sm text-[#0A0A0A]">Entrega Rápida</p>
                  <p className="text-xs text-gray-500">Retire ou receba</p>
                </div>
              </div>
            </div>

            {/* Botões de ação */}
            <div className="space-y-3 pt-4">
              <Button 
                onClick={handleWhatsApp}
                className="w-full bg-[#64BC46] hover:bg-[#56a33c] active:bg-[#4a9f38] text-white py-6 text-lg rounded-full shadow-lg touch-manipulation active:scale-[0.98] transition-all"
                type="button"
              >
                <WhatsAppIcon className="w-6 h-6 mr-2" />
                Consultar Preço via WhatsApp
              </Button>
              
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  onClick={handleAddToQuote}
                  disabled={hasVariations && Object.keys(selectedVariations).length === 0}
                  className={`w-full py-5 rounded-full text-base touch-manipulation active:scale-[0.98] transition-all ${
                    hasVariations && Object.keys(selectedVariations).length === 0
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-[#EA1266] hover:bg-[#d00f58] active:bg-[#b80d4a] text-white'
                  }`}
                  type="button"
                >
                  <Plus size={20} className="mr-2" />
                  Adicionar ({totalSelectedQty})
                </Button>
                <Button 
                  onClick={goToQuote}
                  disabled={hasVariations && Object.keys(selectedVariations).length === 0}
                  variant="outline"
                  className="w-full py-5 rounded-full border-2 border-[#EA1266] text-[#EA1266] hover:bg-[#EA1266] hover:text-white active:bg-[#d00f58] active:text-white text-base touch-manipulation active:scale-[0.98] transition-all"
                  type="button"
                >
                  <ShoppingCart size={20} className="mr-2" />
                  Ir ao Orçamento
                </Button>
              </div>

              <Button 
                variant="outline" 
                onClick={handleShare}
                className="w-full py-4 rounded-full touch-manipulation active:scale-[0.98] transition-all"
                type="button"
              >
                {copied ? <Check size={18} className="mr-2 text-green-600" /> : <Share2 size={18} className="mr-2" />}
                {copied ? 'Link Copiado!' : 'Compartilhar Produto'}
              </Button>
            </div>

            {/* Contato */}
            <div className="bg-pink-50 rounded-xl p-4 border border-pink-100">
              <p className="text-[#EA1266] font-medium flex items-center gap-2">
                <Phone size={18} />
                Dúvidas? Fale conosco: {settings.site_phone}
              </p>
            </div>
          </div>
        </div>

        {/* Produtos Relacionados */}
        {relatedProducts.length > 0 && (
          <section className="mt-16">
            <h2 className="text-2xl font-medium text-[#0A0A0A] mb-8">Você Também Pode Gostar</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {relatedProducts.map((related) => (
                <Link key={related.id} to={`/produto/${related.slug || related.id}`}>
                  <Card className="group overflow-hidden rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                    <div className="aspect-square bg-[#F3F4F6] overflow-hidden">
                      <img
                        src={related.images?.[0] || 'https://placehold.co/400x400?text=Produto'}
                        alt={related.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    </div>
                    <CardContent className="p-4">
                      <Badge className="mb-2 text-xs bg-[#0A0A0A] text-white rounded-full">{related.brand}</Badge>
                      <h3 className="font-medium text-sm text-[#0A0A0A] line-clamp-2 group-hover:text-[#EA1266] transition-colors">
                        {related.name}
                      </h3>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
